/**
 * @fileOverview This file contains the service for add-user module.
 * This is the file where all the business logic goes for add-user component.
 * @module user-management/add-user/add-user.service
 * @author Muhammad Hamza Khan
 * @since January 10th, 2024
 */

import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { Building } from 'src/app/building-managment/building-management-interfaces';
import { IUnitAndAccessPoint, IUser } from '../user-management-interfaces';

@Injectable({
  providedIn: 'root',
})
export class AddUserService {
  private buildingListSubject: BehaviorSubject<Building | null> =
    new BehaviorSubject<Building | null>(null);
  private floorUnits: any = null;
  private selectedFloorUnits: any[] = [];
  private selectedUnitAccessPoint: { units: any[]; accessPoints: any[] } = {
    units: [],
    accessPoints: [],
  };
  private alreadySharedGroups: string[] = [];
  private alreadySelectedGroups: string[] = [];
  private alreadySharedEntitiesMap = new Map();
  private selectedEntitiesMap: Map<string, string> = new Map();

  /**
   * Setter method for buildingList
   * @param buildingList
   * @returns void
   */
  setBuildingList(buildingList: Building): void {
    this.buildingListSubject.next(buildingList);
  }

  /**
   * Setter method for alreadySharedGroups
   * @param groups
   * @returns void
   */
  resetAll() {
    this.floorUnits = null;
    this.selectedFloorUnits = [];
    this.selectedUnitAccessPoint = {
      units: [],
      accessPoints: [],
    };
    this.alreadySharedGroups = [];
    this.alreadySelectedGroups = [];
    this.alreadySharedEntitiesMap = new Map();
    this.selectedEntitiesMap = new Map();
  }

  /**
   * Setter method for floorUnit
   * @param floorUnits
   * @returns void
   */
  setFloorUnits(floorUnits: any): void {
    this.floorUnits = floorUnits;
  }

  /**
   * getter method for getting the count of updated entity count of
   * building
   * @param buildingId
   * @returns number
   */
  getUpdetedBuildingCount(): number {
    return this.getFloorUnits()
      .flat()
      .filter((entity: any) => {
        return entity.status;
      }).length;
  }

  /**
   * Setter method for selectedFloorUnits
   * @param selectedFloorUnits
   * @returns void
   */
  setSelectedFloorUnits(selectedFloorUnits: any[]): void {
    selectedFloorUnits.forEach((unit: any) => {
      if (!unit?.alreadySelected) {
        if (
          unit?.status &&
          !this.entityExists(unit.unitId || unit.accessPointId)
        ) {
          this.addSelectedEntity(unit);
        } else if (
          !unit?.status &&
          this.entityExists(unit.unitId || unit.accessPointId)
        ) {
          this.removeSelectedEntity(unit);
        }
      }
    });
  }

  /**
   * Setter method for setting default selected floor units
   * @param selectedFloorUnits
   * @returns void
   */
  setDefaultSelectedFloorUnits(entity: any): void {
    this.selectedFloorUnits = [];
    this.selectedFloorUnits.push(entity);
  }

  /**
   * Setter method for adding new value in selected map and selectedFloorUnits
   * @param unitId
   * @param buildingId
   * @returns void
   */
  addSelectedEntity(entity: any): void {
    this.selectedEntitiesMap.set(
      entity?.unitId || entity?.accessPointId,
      entity?.buildingId
    );
    this.selectedFloorUnits.push(entity);
  }

  /**
   * Setter method for removing value from selected map and selectedFloorUnits
   * @param entity
   * @returns void
   */
  removeSelectedEntity(entity: any): void {
    this.selectedEntitiesMap.delete(entity?.unitId || entity?.accessPointId);
    this.selectedFloorUnits = this.selectedFloorUnits.filter(
      (floorUnit: any) =>
        floorUnit.unitId !== entity.unitId ||
        floorUnit?.accessPointId !== entity?.accessPointId
    );
  }

  /**
   * Setter method for checking existing unit in selected map
   * @param unitId
   * @returns boolean
   */
  entityExists(id: string): boolean {
    return this.selectedEntitiesMap.has(id);
  }

  /**
   * Setter method for checking existing accesspoint in selected map
   * @param unitId
   * @returns boolean
   */
  accessExists(accessPointId: string): boolean {
    return this.selectedEntitiesMap.has(accessPointId);
  }

  /**
   * getSelectedFloorUnits function responsible for getting the
   * selected floor units.
   * @returns any[]
   * @param selectedFloorUnits
   * @returns any[] | null
   *
   **/
  getSelectedFloorUnits(): any[] {
    return this.selectedFloorUnits;
  }

  /**
   * Getter method for floorUnit
   * @param floorUnit
   * @returns Observable<any[] | null>
   */
  getFloorUnits(): any | null {
    return this.floorUnits;
  }

  /**
   * Getter method for filteredFloorUnits
   * @param floorSelected
   * @param wingsSelected
   * @returns any | null
   */
  getFilteredFloorUnits(
    floorSelected: string[],
    wingsSelected: string[]
  ): any | null {
    let filteredFloorUnits = this.floorUnits;

    if (floorSelected.length > 0 && wingsSelected.length === 0) {
      filteredFloorUnits = this.floorUnits?.filter((floorUnit: any) => {
        const isFloorSelected = floorSelected?.includes(floorUnit.floorName);
        return isFloorSelected;
      });
    } else if (wingsSelected.length > 0 && floorSelected.length === 0) {
      filteredFloorUnits = this.floorUnits?.filter((floorUnit: any) => {
        const isWingSelected = wingsSelected?.includes(floorUnit.wingName);
        return isWingSelected;
      });
    } else if (wingsSelected.length > 0 && floorSelected.length > 0) {
      filteredFloorUnits = this.floorUnits?.filter((floorUnit: any) => {
        const isWingSelected = wingsSelected?.includes(floorUnit.wingName);
        const isFloorSelected = floorSelected?.includes(floorUnit.floorName);
        return isWingSelected && isFloorSelected;
      });
    } else {
      filteredFloorUnits = this.floorUnits;
    }
    return filteredFloorUnits;
  }

  /**
   * Getter method for getting unSelected Floors
   * @returns number
   */
  getUnSelectedFloors(): any[] {
    return (
      this.floorUnits?.flat().filter((floorUnit: any) => {
        if (!floorUnit?.alreadySelected && !floorUnit.status) {
          return floorUnit;
        }
      }) || []
    );
  }

  /**
   * Getter method for getting Selected Floors
   * @returns any[]
   */
  getSelectedFloors(): any[] {
    return (
      this.selectedFloorUnits?.filter((floorUnit: any) => {
        if (!floorUnit?.alreadySelected && floorUnit.status) {
          return floorUnit;
        }
      }) || []
    );
  }

  /**
   * Getter method for buildingList
   * @param buildingList
   * @returns Observable<Building | null>
   */
  getBuildingList(): Observable<Building | null> {
    return this.buildingListSubject.asObservable();
  }

  /**
   * Getter method for selectedUnitAccessPoint
   * @returns { units: any[]; accessPoints: any[] }
   */
  getAlreadySelectedUnitAccessPoint(): IUnitAndAccessPoint {
    return this.selectedUnitAccessPoint;
  }

  /**
   * Getter method for alreadySharedGroups
   * @param none
   * @returns string[]
   */
  getAlreadySharedGroups(): string[] {
    return this.alreadySharedGroups;
  }

  /**
   * Setter method for alreadySelectedGroups
   * @param none
   * @returns void
   */
  getAlreadySelectedGroups(): string[] {
    return this.alreadySelectedGroups;
  }

  /**
   * @description setAlreadySelectedEntities function responsible for existing user
   *              list selection fetched from getUserAccess API.
   * @param user user object
   * @returns void
   */
  setAlreadySelectedEntities(user: IUser): void {
    if (!user) {
      return;
    }

    // Process units
    const units =
      user.unitsAndAccessPoints?.flatMap(
        (entity: any) =>
          entity.units
            ?.filter((unit: any) => unit?.isActive)
            ?.map((unit: any) => ({
              unitId: unit.unitId,
              buildingId: entity.buildingId,
              alreadySelected: true,
              isCompleteAccess: unit.isCompleteAccess,
            })) || []
      ) || [];

    // Process access points
    const accessPoints =
      user.unitsAndAccessPoints?.flatMap(
        (entity: any) =>
          entity.accessPoints
            ?.filter((accessPoint: any) => accessPoint?.isActive)
            ?.map((accessPoint: any) => ({
              accessPointId: accessPoint.accessPointId,
              buildingId: entity.buildingId,
              alreadySelected: true,
            })) || []
      ) || [];
    this.selectedUnitAccessPoint = { units, accessPoints };
    this.alreadySharedGroups = user.groupIds ? [...user.groupIds] : [];
  }

  /**
   * @description setBuildingingEntitiesCount function responsible for setting the
   *             count of units and access points for each building.
   * @param buildingList building list
   * @returns Building  building list with count
   */
  setBuildingingEntitiesCount() {
    const buildingList = this.buildingListSubject.value;
    const unitAndAccesspoints = [
      ...this.selectedUnitAccessPoint.units,
      ...this.selectedUnitAccessPoint.accessPoints,
    ];

    const counts: { [key: string]: number } = {};

    for (const item of unitAndAccesspoints) {
      if (item.buildingId) {
        const buildingId = item.buildingId;
        counts[buildingId] = (counts[buildingId] || 0) + 1;
      }
      this.alreadySharedEntitiesMap.set(
        item.unitId || item.accessPointId,
        item.buildingId
      );
    }

    const updatedBuildingList = buildingList?.map((building: any) => {
      if (building?.buildingId) {
        const buildingId = building.buildingId;

        return {
          ...building,
          count: counts[buildingId] || 0,
        };
      }
      return building;
    });

    this.buildingListSubject.next(updatedBuildingList);
  }

  /**
   * @description getalreadyselectedEntitiesMap function responsible for getting the
   *            already selected map.
   * @returns Map<string, string>
   */
  getAlreadySharedEntities(key: string): string | undefined {
    return this.alreadySharedEntitiesMap.get(key);
  }

  /**
   * @description hasAlreadyShared function responsible for checking if the
   *           already selected map has the key.
   * @returns boolean
   * @param key
   */
  hasAlreadySharedEntities(key: string): boolean {
    return this.alreadySharedEntitiesMap.has(key);
  }

  /**
   * @description hasSelected function responsible for checking if the
   *          selected map has the key.
   * @returns boolean
   * @param key
   * @returns boolean
   */

  hasSelectedEntity(key: string): boolean {
    return this.selectedEntitiesMap.has(key);
  }

  /**
   * @description selctedEntityMapSize function responsible for getting the
   *            selected entiy map size.
   * @returns Map<string, string>
   */
  selctedEntityMapSize(): number {
    return this.selectedEntitiesMap.size;
  }

  /**
   * @description getselectedEntitiesMap function responsible for getting the
   *            selected map.
   * @returns Map<string, string>
   */
  getselectedEntitiesMap(): Map<string, string> {
    return this.selectedEntitiesMap;
  }

  /**
   * @description setSelected function responsible for setting the
   *            selected map.
   * @returns void
   */
  setSelectedEntitiesMap(selectedFloorUnits: any[]) {
    const updatedMap = new Map();
    selectedFloorUnits.forEach((unit: any) => {
      updatedMap.set(unit.unitId, unit.buildingId);
    });
    this.selectedEntitiesMap = updatedMap;
  }
}
